import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CalendarOff, Check, X } from "lucide-react";

const KINDS = {
  vacation: { label: "Vacation",     color: "#0ea5e9" },
  sick:     { label: "Sick day",     color: "#dc2626" },
  flex:     { label: "Flex day",     color: "#d4af37" },
  unpaid:   { label: "Unpaid leave", color: "#71717a" },
};
const STATUS = {
  pending:  { label: "Pending",  bg: "#fef9c3", fg: "#854d0e" },
  approved: { label: "Approved", bg: "#dcfce7", fg: "#166534" },
  declined: { label: "Declined", bg: "#fee2e2", fg: "#991b1b" },
};

export default function ManagerTimeOffQueue() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/clinic/time-off");
      setRows(data || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Unable to load"); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/clinic/time-off/${id}/decision`, { status });
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(""); }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const settled = rows.filter((r) => r.status !== "pending").slice(0, 15);

  return (
    <div className="card-elev mt-6" data-testid="manager-time-off">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarOff className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
          <h3 className="font-bold text-lg">Time-off requests</h3>
        </div>
        <span className="text-xs text-zinc-500">{pending.length} pending · {settled.length} recent</span>
      </div>
      {err && <div className="text-sm text-red-600 mb-2" data-testid="manager-time-off-error">{err}</div>}
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="manager-time-off-empty">No requests yet.</p>
      ) : (
        <>
          {pending.length > 0 && (
            <ul className="divide-y divide-zinc-100">
              {pending.map((r) => {
                const k = KINDS[r.kind]; const s = STATUS[r.status];
                return (
                  <li key={r.request_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`manager-time-off-row-${r.request_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{r.user_name || r.user_email}</div>
                      <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full" style={{ background: `${k.color}22`, color: k.color }}>{k.label}</span>
                        <span>{r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}</span>
                        {r.note && <span className="text-zinc-400">· {r.note}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                      <button onClick={() => decide(r.request_id, "approved")} disabled={busyId === r.request_id}
                              className="btn-ghost text-green-700 text-xs h-9 px-3" data-testid={`manager-time-off-approve-${r.request_id}`}>
                        <Check className="w-4 h-4 mr-1" />Approve
                      </button>
                      <button onClick={() => decide(r.request_id, "declined")} disabled={busyId === r.request_id}
                              className="btn-ghost text-red-600 text-xs h-9 px-3" data-testid={`manager-time-off-decline-${r.request_id}`}>
                        <X className="w-4 h-4 mr-1" />Decline
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {settled.length > 0 && (
            <>
              <div className={`text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold ${pending.length ? "mt-6" : ""}`}>Recent decisions</div>
              <ul className="divide-y divide-zinc-100 mt-1">
                {settled.map((r) => {
                  const k = KINDS[r.kind]; const s = STATUS[r.status];
                  return (
                    <li key={r.request_id} className="py-2 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">{r.user_name || r.user_email}</span>
                        <span className="text-xs text-zinc-500 ml-2">
                          <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full mr-2" style={{ background: `${k.color}22`, color: k.color }}>{k.label}</span>
                          {r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}
                        </span>
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
